import { Check, ChevronDown } from "lucide-react";
import { Badge, BadgeProps } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/utils/shadcn";

export interface InlineSelectOption<T extends string> {
  value: T;
  label: string;
  variant: BadgeProps["variant"];
}

interface InlineSelectProps<T extends string> {
  value: T;
  options: InlineSelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

/**
 * A compact, editable badge: shows the current value as a colored badge and
 * opens a dropdown to change it. Lets a status/priority cell stay narrow while
 * remaining editable inline.
 */
export function InlineSelect<T extends string>({
  value,
  options,
  onChange,
  disabled,
  ariaLabel
}: InlineSelectProps<T>) {
  const current = options.find((option) => option.value === value) ?? options[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className="inline-flex items-center focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Badge variant={current.variant} className="cursor-pointer gap-1">
            {current.label}
            <ChevronDown className="h-3 w-3 opacity-70" />
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => onChange(option.value)}
            className="gap-2"
          >
            <Check
              className={cn("h-4 w-4", option.value === value ? "opacity-100" : "opacity-0")}
            />
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
